<template>
  <validation-provider
    v-slot="{ errors }"
    :rules="rules"
    :mode="validationMode"
  >
    <v-text-field
      v-model="val"
      v-bind="$attrs"
      class="pt-0"
      :label="label"
      :error-messages="errors"
      :required="rules !== ''"
      :type="type"
      :prefix="computedPrefix"
      v-on="$listeners"
      @focus="onFocus"
      @blur="onBlur"
    />
  </validation-provider>
</template>

<script setup lang="ts">
import {
  computed, PropType, ref, watch,
} from 'vue';
import { TranslateResult } from 'vue-i18n';

const props = defineProps(
  {
    validationMode: { type: String, required: false, default: 'aggressive' },
    value: { type: String, required: true },
    rules: { type: String, required: false, default: '' },
    label: { type: [String, Object] as PropType<string|TranslateResult>, required: false, default: '' },
    type: { type: String, required: false, default: 'text' },
    prefix: { type: Object as PropType<string | Array<string>>, required: false, default: undefined },
    hidePrefixOnBlur: { type: Boolean, required: false, default: false },
    stripPrefixFromValue: { type: Boolean, required: false, default: false },
  },
);

const emit = defineEmits
<{(e: 'input', value: string): void,
  (e: 'focus', event: any): void
  (e: 'blur', event: any): void
}>();

const val = ref(props.value);
// eslint-disable-next-line no-nested-ternary
const chosenPrefix = ref(props.stripPrefixFromValue
  ? (typeof props.prefix === 'string' ? props.prefix : props.prefix[0])
  : undefined);

watch(() => props.value, () => {
  if (props.stripPrefixFromValue && props.value === chosenPrefix.value + val.value) {
    return;
  }
  val.value = props.value;
});

watch(val, () => {
  if (props.stripPrefixFromValue && val.value && val.value.length > 0) {
    if (typeof props.prefix === 'string') {
      val.value = val.value.replace(props.prefix, '');
    } else {
      const prefix = props.prefix.find((p) => val.value.startsWith(p)) ?? chosenPrefix.value;
      chosenPrefix.value = prefix;
      val.value = val.value.replace(prefix!, '');
    }
    emit('input', chosenPrefix.value + val.value);
    return;
  }
  emit('input', val.value);
});

const focused = ref(false);
const onFocus = (event: any) => { focused.value = true; emit('focus', event); };
const onBlur = (event: any) => { focused.value = false; emit('blur', event); };

const computedPrefix = computed(() => {
  if (!props.hidePrefixOnBlur) { return chosenPrefix.value; }
  if (props.hidePrefixOnBlur && (focused.value || (val.value ?? '').length > 0)) {
    return chosenPrefix.value;
  }
  return undefined;
});

</script>
